import React, { useState } from 'react'
import { Redirect, useLocation } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import { withRouter } from 'react-router'
import { useAlert } from 'react-alert'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Container from '@material-ui/core/Container'
import TextField from '@material-ui/core/TextField'
import Checkbox from '@material-ui/core/Checkbox'
import CircularProgress from '@material-ui/core/CircularProgress'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import get from 'lodash/get'
import { useAuthorization } from '../../services/authorization'
import { USER_LOGIN } from '../../network/auth/queries'
import { LINKS, EMAIL_REGEX, INVALID_EMAIL_ADDRESS_TEXT, USED_ID } from '../../constants'
import { useStyles } from './styles'
import logo from '../../assets/ojilogo.png'

const useSearchParams = () => {
  return new URLSearchParams(useLocation().search)
}

const LoginPage = ({ history }) => {
  const getUserNameIfProvided = () => {
    try {
      return params.get('username') ? decodeURIComponent(params.get('username')) : ''
    } catch (error) {
      return ''
    }
  }
  const params = useSearchParams()
  const alert = useAlert()
  const classes = useStyles()
  const [userName, setUserName] = useState(getUserNameIfProvided())
  const [userNameError, setUserNameError] = useState(false)
  const [password, setPassword] = useState('')
  const [isRememberMeChecked, setIsRememberMeChecked] = useState(0)
  const [getAuthToken, setAuthTokens] = useAuthorization()

  const [userLogin, { loading }] = useLazyQuery(USER_LOGIN, {
    onError: error => {
      alert.removeAll()
      alert.show(error.message, {
        type: 'error'
      })
    },
    onCompleted: data => {
      const authenticationResult = get(data, `security.authenticate.authenticationResult`, null)
      if (authenticationResult) {
        const { accessToken, refreshToken, userId } = authenticationResult
        setAuthTokens({
          dashboardRefreshToken: refreshToken,
          dashboardAuthToken: accessToken,
          persistMode: isRememberMeChecked
        })
        localStorage.setItem(USED_ID, userId)
        history.push(LINKS.DASHBOARD)
      }
    }
  })

  const handleUserNameChange = event => {
    setUserNameError(false)
    setUserName(event.target.value)
  }

  const handlePasswordChange = event => {
    setPassword(event.target.value)
  }

  const handleCheckRememberMe = () => {
    setIsRememberMeChecked(isRememberMeChecked ? 0 : 1)
  }

  const isEmailValid = () => {
    const isValid = EMAIL_REGEX.test(userName.toLowerCase().trim())
    if (!isValid) {
      setUserNameError(true)
    }

    return isValid
  }

  const handleFormSubmit = e => {
    e.preventDefault()
    if (isEmailValid() && password)
      userLogin({
        variables: {
          userName: userName.toLowerCase().trim(),
          password
        }
      })
  }

  return getAuthToken() ? (
    <Redirect to={LINKS.DASHBOARD} />
  ) : (
    <div className={classes.root}>
      <Grid container direction='column' justifyContent='center' alignItems='center' spacing={8}>
        <Grid container item direction='row' justifyContent='flex-start' alignItems='center'>
          <Grid item xs={3}>
            <img src={logo} alt='logo' />
          </Grid>
        </Grid>
        <Grid container item direction='row' justifyContent='center' alignItems='center'>
          <Grid item xs={8}>
            <Paper className={classes.paper}>
              <Container>
                <Typography variant='h1' component='div' className={classes.formHeader}>
                  Dashboard Login
                </Typography>
                <Typography variant='body1' component='div' className={classes.loginHint}>
                  If you have an admin account, log in below. To access the Emotion Life Lab, please use the Oji mobile app.
                </Typography>
                <form onSubmit={handleFormSubmit}>
                  <div className={classes.inputBlock}>
                    <div className={classes.inputRow}>
                      {userName ? (
                        <Typography variant='h6' component='label' className={classes.label} htmlFor='Email Address'>
                          EMAIL ADDRESS
                        </Typography>
                      ) : (
                        <div className={classes.labelPlaceHolder} />
                      )}
                      <TextField
                        classes={{
                          root: classes.input
                        }}
                        fullWidth
                        id='Email Address'
                        placeholder='Email Address'
                        variant='outlined'
                        name='username'
                        value={userName}
                        onChange={handleUserNameChange}
                        error={userNameError}
                      />
                    </div>
                    {userNameError && (
                      <Typography variant='h6' component='span' className={classes.errorLabel}>
                        {INVALID_EMAIL_ADDRESS_TEXT}
                      </Typography>
                    )}
                    <div className={classes.inputRow}>
                      {password ? (
                        <Typography variant='h6' component='label' className={classes.label} htmlFor='Password'>
                          PASSWORD
                        </Typography>
                      ) : (
                        <div className={classes.labelPlaceHolder} />
                      )}
                      <TextField
                        classes={{
                          root: classes.input
                        }}
                        fullWidth
                        id='Password'
                        placeholder='Password'
                        variant='outlined'
                        name='password'
                        value={password}
                        onChange={handlePasswordChange}
                        type='password'
                      />
                    </div>
                  </div>
                  <div className={classes.buttonBlock}>
                    {loading ? (
                      <div className={classes.loader}>
                        <CircularProgress color='inherit' />
                      </div>
                    ) : (
                      <>
                        <div className={classes.rememberMeBlock}>
                          <Checkbox color='default' checked={Boolean(isRememberMeChecked)} onChange={handleCheckRememberMe} />
                          <Typography
                            variant='body2'
                            component='span'
                            className={isRememberMeChecked ? classes.rememberMeTextFilled : classes.rememberMeText}
                          >
                            Remember me next time
                          </Typography>
                        </div>
                        <Button disabled={!userName || !password} className={classes.signInButton} type='submit'>
                          SIGN IN
                        </Button>
                      </>
                    )}
                  </div>
                  <Typography variant='body1' component='span' className={classes.forgotPassword}>
                    Forgot your password? Please click&nbsp;
                    <a className={classes.forgotPasswordLink} target='_self' href={`#${LINKS.FORGOT_PASSWORD}`}>
                      here.
                    </a>
                  </Typography>
                </form>
              </Container>
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </div>
  )
}

export default withRouter(LoginPage)
